'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import { T } from "gt-next";

// Bar heights for the chart (pre-computed for consistency)
const BAR_HEIGHTS = [18, 24, 12, 31, 8, 22, 35, 14, 27, 19, 11, 38, 16, 25, 9, 33, 21, 13, 29, 17, 36, 10, 23, 15, 32, 20, 7, 26, 14, 41];

// Time-based follower calculation constants
const BASE_DATE = new Date('2024-01-01T00:00:00Z').getTime();
const BASE_TOTAL = 12_000_000;
const FOLLOWERS_PER_DAY = 2_847; // ~2,800/day = ~1M/year realistic growth
const LIVE_RATIO = 0.097; // Live is ~10% of total

/**
 * Calculate follower stats based on current date.
 * Uses day-level granularity to ensure server/client hydration match.
 */
function calculateStats(timestamp: number) {
  const daysSinceBase = Math.floor((timestamp - BASE_DATE) / (1000 * 60 * 60 * 24));
  const total = BASE_TOTAL + daysSinceBase * FOLLOWERS_PER_DAY;
  const live = Math.floor(total * LIVE_RATIO);
  const historical = total - live;
  return {
    total,
    live,
    historical
  };
}

/**
 * Get a stable timestamp for initial render (start of current UTC day).
 * This ensures server and client compute the same value.
 */
function getStableTimestamp(): number {
  const now = new Date();
  // Truncate to start of UTC day for deterministic server/client match
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}
function BarChart() {
  const $ = _c(1);
  const chartWidth = BAR_HEIGHTS.length * 7 - 5;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width={chartWidth} height={41} viewBox={`0 0 ${chartWidth} ${41}`} className="flex-shrink-0">{BAR_HEIGHTS.map(_temp)}</svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(height, index) {
  const isLast = index === BAR_HEIGHTS.length - 1;
  const x = index * 7;
  const y = 41 - height;
  return <rect key={index} x={x} y={y} width={2} height={height} rx={1} fill="white" fillOpacity={isLast ? 0.9 : 0.3} />;
}
export default function FollowerStats() {
  const $ = _c(27);
  const chartWidth = BAR_HEIGHTS.length * 7 - 5;
  const [stats, setStats] = useState(_temp2);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const interval = setInterval(() => {
        setStats(_temp3);
      }, 4000);
      return () => clearInterval(interval);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      maxWidth: chartWidth
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="mb-3 text-[14px] font-semibold leading-[24px] text-white lg:text-[16px]"><T>Followers Gained</T></p>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== stats.total) {
    t4 = stats.total.toLocaleString();
    $[4] = stats.total;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <div className="text-3xl font-bold tracking-tight text-white">{t4}</div>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div><BarChart /></div>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="h-2.5 w-2.5 rounded-[3px] bg-emerald-400" />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center gap-2">{t7}<span className="text-sm text-white/60"><T>Live</T></span></div>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== stats.live) {
    t9 = stats.live.toLocaleString();
    $[11] = stats.live;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t9) {
    t10 = <div className="flex items-center justify-between">{t8}<span className="text-sm font-medium text-white">{t9}</span></div>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="h-2.5 w-2.5 rounded-[3px] bg-white/30" />;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex items-center gap-2">{t11}<span className="text-sm text-white/60"><T>Historical</T></span></div>;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== stats.historical) {
    t13 = stats.historical.toLocaleString();
    $[17] = stats.historical;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t13) {
    t14 = <div className="flex items-center justify-between">{t12}<span className="text-sm font-medium text-white">{t13}</span></div>;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t10 || $[22] !== t14) {
    t15 = <div className="space-y-2 pt-2">{t10}{t14}</div>;
    $[21] = t10;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t15 || $[25] !== t5) {
    t16 = <div className="space-y-3" style={t2}>{t3}{t5}{t6}{t15}</div>;
    $[24] = t15;
    $[25] = t5;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  return t16;
}
function _temp3(prev) {
  const increment = Math.floor(Math.random() * 3) + 1;
  const newTotal = prev.total + increment;
  const newLive = Math.floor(newTotal * LIVE_RATIO);
  return {
    total: newTotal,
    live: newLive,
    historical: newTotal - newLive
  };
}
function _temp2() {
  return calculateStats(getStableTimestamp());
}